import {
  type AppContent, type AgeBand, type AdIdPurpose, AGE_BANDS, AD_ID_PURPOSES, FINANCE_NONE, HEALTH_NONE,
} from '@/lib/play-template';
import { Choice, YesNo, QuestionRow, CheckList, Group, SectionTitle, Note, inputCls } from './controls';

interface Props { value: AppContent; onChange: (v: AppContent) => void }

/** "None" or a hand-typed list of POLICY_RESPONSE_CHOICE_ID_ suffixes. */
function ChoiceIds({ value, noneId, noneLabel, onChange, placeholder }: {
  value: string[]; noneId: string; noneLabel: string; onChange: (v: string[]) => void; placeholder: string;
}) {
  const isNone = value.length === 1 && value[0] === noneId;
  const custom = isNone ? '' : value.join(', ');
  return (
    <div className="space-y-2">
      <Choice<'none' | 'some'>
        value={isNone ? 'none' : 'some'}
        onChange={(v) => onChange(v === 'none' ? [noneId] : [])}
        options={[{ value: 'none', label: noneLabel }, { value: 'some', label: 'Yes, specify' }]}
      />
      {!isNone && (
        <>
          <input
            className={`${inputCls} font-mono text-xs`}
            value={custom}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean))}
          />
          <Note>
            Choice ids as the console names them (<code>POLICY_RESPONSE_CHOICE_ID_…</code> without the prefix).
            Only the "none" answer has been captured from the console; other ids are ticked blind and
            the runner blocks if one is missing.
          </Note>
        </>
      )}
    </div>
  );
}

export function AppContentSection({ value, onChange }: Props) {
  const set = <K extends keyof AppContent>(k: K, v: AppContent[K]) => onChange({ ...value, [k]: v });
  const only18 = value.target_age_groups.length === 1 && value.target_age_groups[0] === '18+';

  return (
    <div className="space-y-4">
      <SectionTitle sub="What the runner enters on Create app and under Policy → App content. Every answer is replayed as written here.">
        App content
      </SectionTitle>

      <Group title="Create app">
        <QuestionRow label="App or game">
          <Choice<AppContent['app_type']>
            value={value.app_type}
            onChange={(v) => set('app_type', v)}
            options={[{ value: 'game', label: 'Game' }, { value: 'app', label: 'App' }]}
          />
        </QuestionRow>
        <QuestionRow label="Free or paid" hint="Cannot be changed from paid to free after publishing.">
          <Choice<AppContent['pricing']>
            value={value.pricing}
            onChange={(v) => set('pricing', v)}
            options={[{ value: 'free', label: 'Free' }, { value: 'paid', label: 'Paid' }]}
          />
        </QuestionRow>
        <p className="text-xs text-content-muted">
          The Developer Program Policies, Play App Signing and US export laws declarations are accepted for every app.
        </p>
      </Group>

      <Group title="Declarations">
        <QuestionRow label="Contains ads" hint="AdMob, or any other ad SDK.">
          <YesNo value={value.contains_ads} onChange={(v) => set('contains_ads', v)} />
        </QuestionRow>

        <QuestionRow
          label="App access"
          hint="Whether parts of the app are behind a login."
          note={value.app_access === 'restricted' && 'Restricted access needs test credentials for the reviewer, which a human has to supply. The runner will hand this job over.'}
        >
          <Choice<AppContent['app_access']>
            value={value.app_access}
            onChange={(v) => set('app_access', v)}
            options={[{ value: 'unrestricted', label: 'All functionality available' }, { value: 'restricted', label: 'Restricted' }]}
          />
        </QuestionRow>

        <div>
          <div className="text-sm text-content mb-1.5">Target age groups</div>
          <CheckList<AgeBand>
            columns={3}
            value={value.target_age_groups}
            onChange={(v) => set('target_age_groups', AGE_BANDS.filter((b) => v.includes(b)))}
            options={AGE_BANDS.map((b) => ({ value: b, label: b === '18+' ? 'Ages 18 and over' : `Ages ${b}` }))}
          />
          {!only18 && (
            <Note>
              Any band under 18 opens the Families / child-directed wizard, which the runner has not captured.
              Only "Ages 18 and over" runs hands-off.
            </Note>
          )}
        </div>

        <QuestionRow
          label="Government app"
          hint="Developed by or on behalf of a government."
          note={value.government_app && 'A government declaration asks for details a human has to enter.'}
        >
          <YesNo value={value.government_app} onChange={(v) => set('government_app', v)} />
        </QuestionRow>

        {value.app_type === 'app' && (
          <QuestionRow
            label="News app"
            hint="Asked of apps, not games."
            note={value.news_app && 'A news app declaration asks for publisher details a human has to enter.'}
          >
            <YesNo value={value.news_app} onChange={(v) => set('news_app', v)} />
          </QuestionRow>
        )}

        <div>
          <div className="text-sm text-content mb-1.5">Financial features</div>
          <ChoiceIds
            value={value.financial_features}
            noneId={FINANCE_NONE}
            noneLabel="No financial features"
            placeholder="e.g. PERSONAL_LOANS, BANKING"
            onChange={(v) => set('financial_features', v)}
          />
        </div>

        <div>
          <div className="text-sm text-content mb-1.5">Health</div>
          <ChoiceIds
            value={value.health_categories}
            noneId={HEALTH_NONE}
            noneLabel="Not a health app"
            placeholder="e.g. MEDICAL_DEVICE"
            onChange={(v) => set('health_categories', v)}
          />
        </div>

        <QuestionRow label="Uses the advertising ID" hint="Any ad or analytics SDK on Android 13+ does.">
          <YesNo value={value.advertising_id.used} onChange={(v) => set('advertising_id', { ...value.advertising_id, used: v })} />
        </QuestionRow>
        {value.advertising_id.used && (
          <div className="pl-4 border-l-2 border-border">
            <div className="text-sm text-content mb-1.5">Why the advertising ID is used</div>
            <CheckList<AdIdPurpose>
              columns={2}
              value={value.advertising_id.purposes}
              onChange={(v) => set('advertising_id', { ...value.advertising_id, purposes: AD_ID_PURPOSES.map((p) => p.id).filter((p) => v.includes(p)) })}
              options={AD_ID_PURPOSES.map((p) => ({ value: p.id, label: p.label }))}
            />
          </div>
        )}
      </Group>
    </div>
  );
}
